import React, { useEffect, useState } from "react";
import { Button, Card, Col, Stack } from "react-bootstrap";
import { faEdit, faTrash } from "@fortawesome/free-solid-svg-icons";
import { deleteBed, patchBed } from "../../../services/httpClient";
import { unsavedChanges } from "../../../App";
import Input from "../../ui/Input";
import IconButton from "../../ui/IconButton";

interface CardState {
  bed: any;
  getItems: any;
  setOrgToSave: any;
  setErrMessage: any;
  setShowToast: any;
  setToastText: any;
  setToastBody: any;
}

const BedManagementCard = ({ bed, getItems, setOrgToSave, setErrMessage, setShowToast, setToastText, setToastBody }: CardState) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [itemRoom, setItemRoom] = useState<string>("");
  const [itemName, setItemName] = useState<string>("");
  const [saveEnabled, setSaveEnabled] = useState(false);

  useEffect(() => {
    if (bed) {
      setItemRoom(bed?.room_key);
      setItemName(bed?.name);
    }
  }, [bed]);

  const onClickEdit = () => {
    setIsEditing(true);
  };

  const onClickEditFinal = () => {
    if (!itemName) {
      setErrMessage("Cannot set null bed name");
      window.scrollTo({
        top: 0,
        behavior: "auto",
      });
      return;
    }
    //get room id from room name
    const roomId = bed?.room_key === itemRoom ? bed?.room_id : undefined;

    const updatedBed = {
      room_id: roomId,
      name: itemName,
    };

    setErrMessage("");

    patchBed(updatedBed, bed?.id)
      .then((res: any) => {
        unsavedChanges.value = false;
        setOrgToSave(false);
        setShowToast(true);
        setToastText("Bed Updated");
        setToastBody(itemName);
        setIsEditing(false);
        getItems(true);
      })
      .catch((err: any) => {
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelEdit = () => {
    unsavedChanges.value = false;
    setOrgToSave(false);
    setIsEditing(false);
    setErrMessage(undefined);
  };

  const onUpdateName = (e: any) => {
    setItemName(e.target.value);

    if (e.target.value.length > 0) {
      setSaveEnabled(true);
      unsavedChanges.value = true;
      setOrgToSave(true);
      setErrMessage(undefined);
    } else {
      setSaveEnabled(false);
      unsavedChanges.value = false;
      setOrgToSave(false);
    }
  };

  const onClickDelete = (e: any) => {
    e.stopPropagation();
    setIsDeleting(true);
  };

  const onClickDeleteFinal = () => {
    deleteBed(bed?.id)
      .then((res: any) => {
        setShowToast(true);
        setToastText("Bed Deleted");
        setToastBody(itemName);
        setIsDeleting(false);
        getItems();
      })
      .catch((err: any) => {
        setIsDeleting(false);
        setErrMessage(err?.response?.data?.msg);
        window.scrollTo({
          top: 0,
          behavior: "auto",
        });
      });
  };

  const onClickCancelDelete = () => {
    setIsDeleting(false);
    setErrMessage(undefined);
  };

  const renderEdit = () => {
    return (
      <Card key={bed.name} className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Col>
              <Input label="Name" type="text" value={itemName} onUpdate={(e: any) => onUpdateName(e)} required={true} />
            </Col>
            <Button className="app-secondary-color button" onClick={onClickEditFinal} disabled={!saveEnabled}>
              Save
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={onClickCancelEdit}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  const renderDelete = () => {
    return (
      <Card key={bed.name} className="bg-light">
        <Card.Body>
          <Stack direction="horizontal" gap={3}>
            <Stack style={{ justifyContent: "center" }} gap={2}>
              <Card.Subtitle>Are you sure you want to delete {bed?.name}?</Card.Subtitle>
            </Stack>
            <Button className="app-danger-color button" onClick={onClickDeleteFinal}>
              Confirm Delete
            </Button>
            <div className="vr" />
            <Button className="app-primary-color button" onClick={onClickCancelDelete}>
              Cancel
            </Button>
          </Stack>
        </Card.Body>
      </Card>
    );
  };

  return (
    <>
      {isEditing ? (
        <>{renderEdit()}</>
      ) : isDeleting ? (
        <>{renderDelete()}</>
      ) : (
        <Card className="bg-light">
          <Card.Body className="p-2">
            <Stack direction="horizontal" gap={3}>
              <Stack gap={2} className="align-self-center">
                {bed?.room_name && <small className="text-muted">{bed?.room_name}</small>}
                <small>{bed?.name}</small>
              </Stack>
              <IconButton onClickButton={onClickEdit} tooltipText={"Edit"} icon={faEdit} />
              <div className="vr" />
              <IconButton onClickButton={onClickDelete} tooltipText={"Delete"} icon={faTrash} />
            </Stack>
          </Card.Body>
        </Card>
      )}
    </>
  );
};

export default BedManagementCard;
